import { useState, useEffect, useCallback } from 'react'
import { Link2, Copy, Check, Eye, Pencil, Loader2, UserX, Users, Ban } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import type { Project } from '../lib/supabase'
import {
  createShareLink,
  getActiveShareLinks,
  revokeShareLinks,
  removeProjectMember,
  buildShareUrl,
  type ShareLink
} from '../services/shareLinkService'
import { getTeamMembersWithProfiles } from '../services/teamService'

interface ShareMember {
  user_id: string
  role: string
  nickname: string | null
  email: string | null
}

interface ShareLinkSectionProps {
  project: Project
  /** リンク発行でプロジェクトが共有化された後に呼ばれる（プロジェクト一覧の再取得用） */
  onChanged?: () => Promise<void> | void
}

// プロジェクト設定モーダル内の「共有リンク」セクション
// リンクを知っているログインユーザーがプロジェクトに参加できる（閲覧 or 編集）
export default function ShareLinkSection({ project, onChanged }: ShareLinkSectionProps) {
  const { user } = useAuth()
  const [links, setLinks] = useState<ShareLink[]>([])
  const [members, setMembers] = useState<ShareMember[]>([])
  const [newRole, setNewRole] = useState<'editor' | 'viewer'>('editor')
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isOwner = user?.id === project.user_id

  const loadData = useCallback(async () => {
    if (!project.team_id) {
      setLinks([])
      setMembers([])
      return
    }
    try {
      const [linkList, memberList] = await Promise.all([
        getActiveShareLinks(project.id),
        getTeamMembersWithProfiles(project.team_id)
      ])
      setLinks(linkList)
      setMembers(
        (memberList as any[]).map(m => ({
          user_id: m.user_id,
          role: m.role,
          nickname: m.nickname,
          email: m.email
        }))
      )
    } catch (e) {
      console.error('Failed to load share data:', e)
    }
  }, [project.id, project.team_id])

  useEffect(() => {
    loadData()
  }, [loadData])

  if (!isOwner) return null

  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      const link = await createShareLink(project.id, newRole)
      setLinks(prev => {
        const without = prev.filter(l => l.token !== link.token)
        return [link, ...without]
      })
      // チームが自動作成された場合にプロジェクト情報を更新
      await onChanged?.()
    } catch (e) {
      console.error('Failed to create share link:', e)
      setError('リンクの発行に失敗しました')
    }
    setCreating(false)
  }

  const handleCopy = async (link: ShareLink) => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(link.token))
      setCopiedToken(link.token)
      setTimeout(() => setCopiedToken(null), 2000)
    } catch {
      setError('コピーに失敗しました')
    }
  }

  const handleRevoke = async () => {
    if (!window.confirm('共有リンクを無効化しますか？\n（すでに参加しているメンバーはそのまま残ります）')) return
    setRevoking(true)
    setError(null)
    try {
      await revokeShareLinks(project.id)
      setLinks([])
    } catch (e) {
      console.error('Failed to revoke share links:', e)
      setError('無効化に失敗しました')
    }
    setRevoking(false)
  }

  const handleRemoveMember = async (member: ShareMember) => {
    const name = member.nickname || member.email || 'このメンバー'
    if (!window.confirm(`${name} をプロジェクトから削除しますか？`)) return
    try {
      await removeProjectMember(project.id, member.user_id)
      setMembers(prev => prev.filter(m => m.user_id !== member.user_id))
    } catch (e) {
      console.error('Failed to remove member:', e)
      setError('メンバーの削除に失敗しました')
    }
  }

  const guestMembers = members.filter(m => m.user_id !== project.user_id)

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">共有リンク</span>
      </div>

      <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
        {error && (
          <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
            {error}
          </div>
        )}

        {/* 発行済みリンク */}
        {links.length > 0 ? (
          <>
            {links.map(link => (
              <div key={link.token} className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  {link.role === 'viewer' ? (
                    <><Eye className="w-3.5 h-3.5" /><span>閲覧のみ</span></>
                  ) : (
                    <><Pencil className="w-3.5 h-3.5" /><span>編集可</span></>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={buildShareUrl(link.token)}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-2 py-1.5 text-xs border rounded bg-white text-gray-600"
                  />
                  <button
                    onClick={() => handleCopy(link)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex-shrink-0"
                  >
                    {copiedToken === link.token ? (
                      <><Check className="w-3.5 h-3.5" />コピー済</>
                    ) : (
                      <><Copy className="w-3.5 h-3.5" />コピー</>
                    )}
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              {revoking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
              リンクを無効化する
            </button>
          </>
        ) : (
          // リンク未発行: 権限を選んで発行
          <div className="space-y-2">
            <p className="text-xs text-gray-500">
              リンクを知っている人（要ログイン）がこのプロジェクトに参加できます
            </p>
            <div className="flex items-center gap-2">
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as 'editor' | 'viewer')}
                className="px-2 py-1.5 text-xs border rounded bg-white"
              >
                <option value="editor">編集可</option>
                <option value="viewer">閲覧のみ</option>
              </select>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                共有リンクを発行
              </button>
            </div>
          </div>
        )}

        {/* 参加メンバー一覧 */}
        {guestMembers.length > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-2">
              <Users className="w-3.5 h-3.5" />
              <span>参加メンバー（{guestMembers.length}人）</span>
            </div>
            <div className="space-y-1">
              {guestMembers.map(member => (
                <div key={member.user_id} className="flex items-center justify-between px-2 py-1.5 bg-white rounded border">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-gray-800 truncate">
                      {member.nickname || member.email || '（名前未設定）'}
                    </span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      {member.role === 'viewer' ? '閲覧' : '編集'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveMember(member)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                    title="メンバーを削除"
                  >
                    <UserX className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
